import LanguageIcon from '@mui/icons-material/Language';
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

// Interface per le props del componente AutocompleteFilter
// interface AutocompleteFilterProps extends FilterComponentProps {
//     filterKey: string;
//     label: string;
//     placeholder?: string;
//     options: any[]; // Array di oggetti (può essere parziale per lazy loading)
//     getOptionValue: (option: any) => string;
//     getOptionLabel: (option: any) => string;
//     allowGlobal?: boolean;
//     multiple?: boolean;
//     disabled?: boolean; // Nuova prop per disabilitare il componente
//     // Nuove props per lazy loading
//     enableLazyLoading?: boolean;
//     hasMore?: boolean; // Indica se ci sono più elementi da caricare
//     isLoading?: boolean; // Indica se è in corso il caricamento
//     onLoadMore?: () => void; // Callback per caricare più elementi
//     loadMoreThreshold?: number; // Numero di elementi dalla fine per triggerare il caricamento (default: 5)
// }

export default function AutocompleteFilter({ 
    filterKey, 
    label, 
    placeholder,
    options = [],
    getOptionValue,
    getOptionLabel,
    allowGlobal = false,
    multiple = false,
    disabled = false, // Nuovo prop con valore di default false
    onFilterChange, 
    onFilterReset,
    currentFilter,
    // Nuove props per lazy loading
    enableLazyLoading = false, // Abilita/disabilita la funzionalità
    hasMore = false, // Indica se ci sono ancora elementi da caricare
    isLoading = false, // Stato di caricamento
    onLoadMore, // Callback per caricare più elementi
    loadMoreThreshold = 5   // Soglia per triggerare il caricamento (default: 5 elementi dalla fine)
}) {
    const [selectedValue, setSelectedValue] = useState(null);
    const [open, setOpen] = useState(false);
    const listboxRef = useRef(null);
    const loadingTriggered = useRef(false);

    // Inizializza il valore selezionato basandoti sul currentFilter
    useEffect(() => {
        if (currentFilter?.value) {
            if (multiple) {
                const values = currentFilter.value.split(',');
                const selectedOptions = options.filter(option => 
                    values.includes(getOptionValue(option))
                );
                setSelectedValue(selectedOptions);
            } else {
                const selectedOption = options.find(option => 
                    getOptionValue(option) === currentFilter.value
                );
                setSelectedValue(selectedOption || null);
            }
        } else {
            setSelectedValue(multiple ? [] : null);
        }
    }, [currentFilter, options, getOptionValue, multiple]);

    // Reset quando viene chiamato dal parent
    useEffect(() => {
        if (!currentFilter) {
            setSelectedValue(multiple ? [] : null);
        }
    }, [currentFilter, multiple]);

    // Reset del flag di loading quando cambiano le opzioni
    useEffect(() => {
        loadingTriggered.current = false;
    }, [options]);

    // Gestisce lo scroll per il lazy loading
    const handleScroll = useCallback((event) => {
        if (!enableLazyLoading || !hasMore || isLoading || loadingTriggered.current || !onLoadMore || disabled) {
            return;
        }

        const listbox = event.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = listbox;
        
        // Calcola se siamo vicini alla fine (basandoti sul threshold)
        const remainingItems = options.length - Math.floor(scrollTop / 48); // 48px è l'altezza approssimativa di un'opzione
        
        if (remainingItems <= loadMoreThreshold) {
            loadingTriggered.current = true;
            onLoadMore();
        }
    }, [enableLazyLoading, hasMore, isLoading, onLoadMore, loadMoreThreshold, options.length, disabled]);

    const handleValueChange = (newValue) => {
        if (disabled) return; // Previene i cambiamenti quando è disabilitato
        
        setSelectedValue(newValue);
        
        if (newValue && onFilterChange) {
            let filterValue;
            let filterLabel;
            
            if (multiple) {
                if (newValue.length > 0) {
                    filterValue = newValue.map(option => getOptionValue(option)).join(',');
                    filterLabel = newValue.map(option => getOptionLabel(option)).join(', ');
                } else {
                    onFilterChange(null);
                    return;
                }
            } else {
                filterValue = getOptionValue(newValue);
                filterLabel = getOptionLabel(newValue);
            }
            
            const filter = {
                key: filterKey,
                value: filterValue,
                label: filterLabel,
                global: allowGlobal
            };
            onFilterChange(filter);
        } else if (!newValue && onFilterChange) {
            onFilterChange({
                key: filterKey,
                value: undefined,
            });
        }
    };

    // Componente personalizzato per il rendering del listbox con lazy loading
    const CustomListbox = (props) => {
        return (
            <ul 
                {...props} 
                ref={listboxRef}
                onScroll={enableLazyLoading && !disabled ? handleScroll : undefined}
                style={{ 
                    ...props.style,
                    maxHeight: 300, // Altezza massima per abilitare lo scroll
                    overflowY: 'auto'
                }}
            >
                {props.children}
                {/* Indicatore di caricamento alla fine della lista */}
                {enableLazyLoading && isLoading && !disabled && (
                    <Box 
                        display="flex" 
                        justifyContent="center" 
                        alignItems="center" 
                        py={2}
                        sx={{ borderTop: '1px solid #e0e0e0' }}
                    >
                        <CircularProgress size={20} />
                        <Box ml={1} fontSize="0.875rem" color="text.secondary">
                            Caricamento...
                        </Box>
                    </Box>
                )}
                {/* Messaggio quando non ci sono più elementi da caricare */}
                {enableLazyLoading && !hasMore && !isLoading && options.length > 0 && !disabled && (
                    <Box 
                        display="flex" 
                        justifyContent="center" 
                        py={1}
                        sx={{ borderTop: '1px solid #e0e0e0' }}
                    >
                        <Box fontSize="0.75rem" color="text.secondary">
                            Tutti gli elementi sono stati caricati
                        </Box>
                    </Box>
                )}
            </ul>
        );
    };

    return (
        <div className="p-2 border-b">
            <Autocomplete
                multiple={multiple}
                options={options}
                getOptionLabel={getOptionLabel}
                getOptionKey={getOptionValue}
                value={selectedValue}
                open={open && !disabled} // Previene l'apertura quando è disabilitato
                onOpen={() => !disabled && setOpen(true)} // Previene l'apertura quando è disabilitato
                onClose={() => setOpen(false)}
                onChange={(event, newValue) => handleValueChange(newValue)}
                disabled={disabled} // Passa la prop disabled all'Autocomplete di MUI
                isOptionEqualToValue={(option, value) => 
                    getOptionValue(option) === getOptionValue(value)
                }
                loading={enableLazyLoading && isLoading && options.length === 0 && !disabled}
                // Usa il componente personalizzato per il listbox solo se lazy loading è abilitato
                ListboxComponent={enableLazyLoading ? CustomListbox : undefined}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder={placeholder}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={disabled} // Passa la prop disabled anche al TextField
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: allowGlobal ? (
                                <>
                                    <LanguageIcon 
                                        fontSize="small" 
                                        sx={{ 
                                            mr: 1, 
                                            color: disabled ? 'action.disabled' : 'action.active' 
                                        }} 
                                    />
                                    {params.InputProps.startAdornment}
                                </>
                            ) : params.InputProps.startAdornment,
                            endAdornment: (
                                <>
                                    {enableLazyLoading && isLoading && options.length === 0 && !disabled ? (
                                        <CircularProgress color="inherit" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                noOptionsText={isLoading ? "Caricamento..." : "Nessuna opzione disponibile"}
                clearText="Cancella"
                closeText="Chiudi"
                openText="Apri"
            />
        </div>
    );
}